import moment from "moment";
import Section from "../../parts/Section";
import { brlToInt, formatAddress, intToBrl, stateMap } from "../../utils";
import _ from "lodash";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useState } from "react";
import Api from "../../Api";
import {
  MaskedTextBlock,
  SelectBlock,
  TextBlock,
} from "../../parts/FormBlocks";
import classNames from "classnames";
import MoneyInput from "../../parts/MoneyInput";
import { RentContract, RentContractClient } from "../../types";

const FinanceContractDetails = ({
  contract,
  reload,
}: {
  contract: RentContract;
  reload: () => void;
}) => {
  const services = _.upperFirst(
    _.filter([
      contract.serviceAlarm ? "alarme" : false,
      contract.serviceCamera ? "camera" : false,
    ]).join(" e ")
  );

  return (
    <Section className="p-4">
      <div className="flex flex-col gap-4 pb-32">
        <div className="flex gap-2">
          <Info
            label="Início"
            info={moment.utc(contract.startDate).format("DD/MM/YYYY")}
          />
          <Info label="Vencimento" info={`Dia ${contract.expirationDay}`} />
          <Info label="Serviços" info={services} />
        </div>

        <State contract={contract} reload={reload} />

        <Amount contract={contract} reload={reload} />

        <Client contract={contract} reload={reload} />

        <Note contract={contract} reload={reload} internal={false} />

        <Emails contract={contract} reload={reload} />

        <Note contract={contract} reload={reload} internal={true} />
      </div>
    </Section>
  );
};

export default FinanceContractDetails;

const State = ({
  contract,
  reload,
}: {
  contract: RentContract;
  reload: () => void;
}) => {
  const [edit, setEdit] = useState(false);
  const [saving, setSaving] = useState(false);

  const suspend = () => {
    setSaving(true);
    Api.financeUpdateField(contract.uuid, "suspended", true).then(() => [
      reload(),
      setEdit(false),
      setSaving(false),
    ]);
  };

  return (
    <div
      className={classNames("border", {
        "border-green-700": !contract.suspended && !edit,
        "border-red-700": contract.suspended && !edit,
      })}
    >
      {!edit && (
        <div className="flex items-stretch">
          <div
            className={classNames("py-2 px-4 flex-1", {
              "bg-green-50 text-green-800": !contract.suspended,
              "bg-red-50 text-red-700": contract.suspended,
            })}
          >
            <div className="font-semibold">Situação</div>
            <div>{contract.suspended ? "Suspenso" : "Ativo"}</div>
          </div>
          {!contract.suspended && (
            <div
              className="border-l border-green-700 p-4 bg-green-50 hover:bg-green-100 text-green-800 cursor-pointer flex items-center"
              onClick={() => setEdit(true)}
            >
              <FontAwesomeIcon icon="pen-to-square" />
            </div>
          )}
        </div>
      )}

      {edit && (
        <div className="p-6 flex flex-col gap-4 items-center">
          <div className="font-semibold">Suspender contrato?</div>
          <div className="flex gap-4">
            <Button
              type="white"
              label="Cancelar"
              onClick={() => setEdit(false)}
              disabled={saving}
            />

            <Button
              type="red"
              label="Suspender"
              onClick={suspend}
              disabled={saving}
            />
          </div>
        </div>
      )}
    </div>
  );
};

const Amount = ({
  contract,
  reload,
}: {
  contract: RentContract;
  reload: () => void;
}) => {
  const [edit, setEdit] = useState(false);
  const [amount, setAmount] = useState(intToBrl(contract.amount));
  const [saving, setSaving] = useState(false);

  const update = () => {
    setSaving(true);
    Api.financeUpdateField(contract.uuid, "amount", brlToInt(amount)).then(
      () => [reload(), setEdit(false), setSaving(false)]
    );
  };

  return (
    <div className="border">
      {!edit && (
        <div className="flex items-stretch">
          <div className="py-2 px-4 flex-1">
            <div className="font-semibold">Valor</div>
            <div>{intToBrl(contract.amount)}</div>
          </div>
          {!contract.suspended && (
            <div
              className="border-l p-4 hover:bg-slate-50 cursor-pointer flex items-center"
              onClick={() => setEdit(true)}
            >
              <FontAwesomeIcon icon="pen-to-square" />
            </div>
          )}
        </div>
      )}

      {edit && (
        <div className="p-6 flex flex-col gap-4 items-center">
          <div className="font-semibold">Novo valor</div>
          <MoneyInput
            className="border rounded py-1 px-3 w-44"
            value={amount}
            onChange={setAmount}
            autoFocus={true}
          />
          <div className="flex gap-4">
            <Button
              type="white"
              label="Cancelar"
              onClick={() => [
                setEdit(false),
                setAmount(intToBrl(contract.amount)),
              ]}
              disabled={saving}
            />

            <Button
              type="blue"
              label="Atualizar"
              onClick={update}
              disabled={saving}
            />
          </div>
        </div>
      )}
    </div>
  );
};

const Emails = ({
  contract,
  reload,
}: {
  contract: RentContract;
  reload: () => void;
}) => {
  const [edit, setEdit] = useState(false);
  const [emails, setEmails] = useState(contract.emails);
  const [saving, setSaving] = useState(false);

  const addEmail = () => setEmails([...emails, ""]);
  const deleteEmail = (index: number) => () =>
    setEmails([...emails.slice(0, index), ...emails.slice(index + 1)]);
  const updateEmail = (index: number, value: string) =>
    setEmails([...emails.slice(0, index), value, ...emails.slice(index + 1)]);

  const update = (event: React.FormEvent) => {
    setSaving(true);
    event.preventDefault();
    Api.financeUpdateField(contract.uuid, "emails", emails).then(() => [
      reload(),
      setEdit(false),
      setSaving(false),
    ]);
  };

  return (
    <div className="border">
      {!edit && (
        <div className="flex items-stretch">
          <div className="py-2 px-4 flex-1">
            <div className="font-semibold mb-1">Emails cadastrados</div>
            <ul className="text-sm list-disc pl-4">
              {emails.map((email, i) => (
                <li key={i}>{email}</li>
              ))}
            </ul>
          </div>
          {!contract.suspended && (
            <div
              className="border-l p-4 hover:bg-slate-50 cursor-pointer flex items-center"
              onClick={() => setEdit(true)}
            >
              <FontAwesomeIcon icon="pen-to-square" />
            </div>
          )}
        </div>
      )}

      {edit && (
        <form className="flex flex-col gap-2 w-full p-4" onSubmit={update}>
          {emails.map((email, i) => (
            <div key={i} className="flex gap-2 text-sm">
              <input
                className="border rounded py-1 px-3 flex-1"
                type="email"
                value={email}
                onChange={(event) => updateEmail(i, event.target.value)}
                required
              />

              <button
                type="button"
                className="border rounded px-2 text-gray-600 hover:bg-slate-50 disabled:text-gray-400 disabled:bg-gray-100 disabled:border-transparent"
                disabled={emails.length <= 1 || saving}
                onClick={deleteEmail(i)}
              >
                <FontAwesomeIcon icon="trash" />
              </button>
            </div>
          ))}

          <div className="flex flex-col gap-2">
            <Button
              type="white"
              label="+ Adicionar novo"
              onClick={addEmail}
              className="w-full"
              disabled={saving}
            />

            <div className="flex gap-2">
              <Button
                type="white"
                label="Cancelar"
                onClick={() => [setEdit(false), setEmails(contract.emails)]}
                className="basis-1/2"
                disabled={saving}
              />

              <Button
                type="blue"
                label="Atualizar"
                className="basis-1/2"
                submit={true}
                disabled={saving}
              />
            </div>
          </div>
        </form>
      )}
    </div>
  );
};

const Client = ({
  contract,
  reload,
}: {
  contract: RentContract;
  reload: () => void;
}) => {
  const [edit, setEdit] = useState(false);
  const [saving, setSaving] = useState(false);
  const [client, setClient] = useState<RentContractClient>(
    _.pick(contract.client, [
      "name",
      "type",
      "document",
      "zipcode",
      "state",
      "city",
      "street",
      "number",
    ])
  );

  const change = (key: keyof RentContractClient) => (value: string) =>
    setClient((client) => ({ ...client, [key]: value }));

  const submit = (event: React.FormEvent) => {
    setSaving(true);
    event.preventDefault();
    Api.financeUpdateField(contract.uuid, "client", client).then(() => [
      reload(),
      setEdit(false),
      setSaving(false),
    ]);
  };

  return (
    <div className="border">
      {!edit && (
        <div className="flex items-stretch">
          <div className="py-2 px-4 flex-1">
            <div className="font-semibold mb-1">Dados do cliente</div>
            <ul className="text-sm list-disc pl-4">
              <li>{contract.client.name}</li>
              <li>{contract.client.document}</li>
              <li>{formatAddress(contract.client)}</li>
            </ul>
          </div>
          {!contract.suspended && (
            <div
              className="border-l p-4 hover:bg-slate-50 cursor-pointer flex items-center"
              onClick={() => setEdit(true)}
            >
              <FontAwesomeIcon icon="pen-to-square" />
            </div>
          )}
        </div>
      )}

      {edit && (
        <form className="p-4 flex flex-col gap-4" onSubmit={submit}>
          <TextBlock
            label={"Nome"}
            value={client.name}
            onChange={change("name")}
          />

          <div className="flex flex-col gap-1">
            <label className="text-sm font-bold">Tipo</label>
            <div className="flex text-sm font-bold">
              <label className="flex gap-3 border py-1 px-3 hover:bg-slate-100 cursor-pointer rounded-l border-r-0">
                <input
                  type="radio"
                  checked={client.type === "pf"}
                  onChange={(e) => [
                    change("type")(e.target.value),
                    change("document")(""),
                  ]}
                  value="pf"
                />{" "}
                <span>PF</span>
              </label>

              <label className="flex gap-3 border py-1 px-3 hover:bg-slate-100 cursor-pointer rounded-r">
                <input
                  type="radio"
                  checked={client.type === "pj"}
                  onChange={(e) => [
                    change("type")(e.target.value),
                    change("document")(""),
                  ]}
                  value="pj"
                />{" "}
                <span>PJ</span>
              </label>
            </div>
          </div>

          <MaskedTextBlock
            label={client.type === "pf" ? "CPF" : "CNPJ"}
            value={client.document}
            onChange={change("document")}
            mask={
              client.type === "pf" ? "999.999.999-99" : "99.999.999/9999-99"
            }
          />

          <MaskedTextBlock
            label="CEP"
            value={client.zipcode}
            onChange={change("zipcode")}
            mask="99999-999"
          />

          <SelectBlock
            label={"Estado"}
            value={client.state}
            onChange={change("state")}
            options={stateMap}
          />

          <TextBlock
            label={"Cidade"}
            value={client.city}
            onChange={change("city")}
          />

          <TextBlock
            label={"Logradouro"}
            value={client.street}
            onChange={change("street")}
          />

          <TextBlock
            label={"Número"}
            value={client.number}
            onChange={change("number")}
          />

          <div className="flex flex-col items-center">
            <div className="flex gap-4">
              <Button
                type="white"
                label="Cancelar"
                onClick={() => [setEdit(false), setClient(contract.client)]}
                disabled={saving}
              />

              <Button
                type="blue"
                label="Atualizar"
                disabled={saving}
                submit={true}
              />
            </div>
          </div>
        </form>
      )}
    </div>
  );
};

const Note = ({
  contract,
  reload,
  internal,
}: {
  contract: RentContract;
  reload: () => void;
  internal: boolean;
}) => {
  const [edit, setEdit] = useState(false);
  const [text, setText] = useState(
    internal ? contract.note : contract.noteClient
  );
  const [saving, setSaving] = useState(false);

  const update = () => {
    setSaving(true);
    Api.financeUpdateField(
      contract.uuid,
      internal ? "note" : "noteClient",
      text
    ).then(() => [reload(), setEdit(false), setSaving(false)]);
  };

  return (
    <div className="border">
      {!edit && (
        <div className="flex items-stretch">
          <div className="py-2 px-4 flex-1">
            <div className="font-semibold mb-1">
              {internal ? "Observação interna" : "Observação para o cliente"}
            </div>
            {text && (
              <div className="text-gray-600 italic leading-tight tracking-tight">
                {text}
              </div>
            )}
            {!text && <div className="italic text-gray-400">Nenhuma</div>}
          </div>
          {(internal || !contract.suspended) && (
            <div
              className="border-l p-4 hover:bg-slate-50 cursor-pointer flex items-center"
              onClick={() => setEdit(true)}
            >
              <FontAwesomeIcon icon="pen-to-square" />
            </div>
          )}
        </div>
      )}

      {edit && (
        <div className="p-4 flex flex-col gap-4 items-center">
          <textarea
            className="border rounded py-1 px-3 w-full"
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={6}
            autoFocus
          />

          <div className="flex gap-4">
            <Button
              type="white"
              label="Cancelar"
              onClick={() => [
                setEdit(false),
                setText(internal ? contract.note : contract.noteClient),
              ]}
              disabled={saving}
            />

            <Button
              type="blue"
              label="Atualizar"
              onClick={update}
              disabled={saving}
            />
          </div>
        </div>
      )}
    </div>
  );
};

const Button = ({
  type,
  label,
  onClick,
  className,
  submit,
  disabled,
}: {
  type: "white" | "red" | "blue";
  label: string;
  onClick?: () => void;
  disabled?: boolean;
  className?: string;
  submit?: boolean;
}) => {
  const classes = {
    white: "hover:bg-slate-50",
    red: "text-white font-extrabold bg-red-700 hover:bg-red-600 border-transparent",
    blue: "text-white font-extrabold bg-sky-600 hover:bg-sky-500 border-transparent",
  }[type];

  return (
    <button
      type={submit ? "submit" : "button"}
      className={`border rounded py-1 px-3 font-bold text-sm ${classes} ${className}`}
      onClick={onClick}
      disabled={disabled}
    >
      {label}
    </button>
  );
};

const Info = ({ label, info }: { label: string; info: string }) => (
  <div className="p-2 text-sm border basis-1/3">
    <div className="font-semibold mb-1">{label}</div>
    {info && <div>{info}</div>}
    {!info && <div className="italic text-gray-400">nenhum</div>}
  </div>
);
